import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Community {
  id: string;
  name: string;
  display_name: string;
  icon?: string;
  color?: string;
  gradient?: string;
  is_fomo_official: boolean;
}

interface CommunityBadgesProps {
  communities: Community[];
  className?: string;
  onCommunityClick?: (community: Community) => void;
  variant?: 'default' | 'compact' | 'inline';
}

export function CommunityBadges({ 
  communities, 
  className,
  onCommunityClick,
  variant = 'default'
}: CommunityBadgesProps) {
  if (!communities || communities.length === 0) return null;

  const lighten = (hex?: string, amount = 0.2) => {
    if (!hex) return '#FFFFFF';
    const m = hex.trim();
    const h = m.startsWith('#') ? m.slice(1) : m;
    if (![3,6].includes(h.length)) return '#FFFFFF';
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const r = parseInt(n.slice(0,2), 16);
    const g = parseInt(n.slice(2,4), 16);
    const b = parseInt(n.slice(4,6), 16);
    const lr = Math.min(255, Math.floor(r + (255 - r) * amount));
    const lg = Math.min(255, Math.floor(g + (255 - g) * amount));
    const lb = Math.min(255, Math.floor(b + (255 - b) * amount));
    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(lr)}${toHex(lg)}${toHex(lb)}`;
  };

  const handleClick = (community: Community) => {
    if (onCommunityClick) {
      onCommunityClick(community);
    }
  };

  if (variant === 'inline') {
    // Inline variant for showing in username area
    return (
      <span className={cn('inline-flex items-center gap-1 text-sm font-medium', className)}>
        <span className="mx-1 text-[#9CA3AF]">→</span>
        {communities.map((community, index) => {
          const baseColor = (community.gradient && /#[0-9A-Fa-f]{3,6}/.test(community.gradient))
            ? (community.gradient.match(/#[0-9A-Fa-f]{3,6}/)?.[0] ?? '#A78BFA')
            : (community.color?.trim() || '#A78BFA');
          const textColor = lighten(baseColor, 0.45);

          return (
            <span key={community.id} className="inline-flex items-center gap-1">
              {index > 0 && <span className="text-[#9CA3AF]">,</span>}
              <button
                onClick={() => handleClick(community)}
                className="inline-flex items-center gap-1 transition-colors text-sm font-semibold"
                style={{ color: textColor }}
              >
                {community.is_fomo_official && (
                  <Sparkles className="w-3 h-3" style={{ color: textColor }} />
                )}
                <span>#{community.name}</span>
              </button>
            </span>
          );
        })}
      </span>
    );
  }

  if (variant === 'compact') {
    // Compact variant for smaller displays
    return (
      <div className={cn("flex flex-wrap gap-1", className)}>
        {communities.map((community) => (
          <button
            key={community.id}
            onClick={() => handleClick(community)}
            className={cn(
              "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full",
              "text-xs font-medium transition-all hover:scale-105",
              "bg-gradient-to-r hover:shadow-sm"
            )}
            style={{
              background: community.gradient || community.color || 'hsl(var(--muted))',
              color: community.gradient || community.color ? 'white' : 'inherit'
            }}
          >
            {community.is_fomo_official && (
              <Sparkles className="w-2.5 h-2.5" />
            )}
            {community.icon && <span>{community.icon}</span>}
            <span>{community.display_name}</span>
          </button>
        ))}
      </div>
    );
  }

  // Default variant with full badges
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {communities.map((community) => (
        <Badge
          key={community.id}
          variant="outline"
          className={cn(
            "cursor-pointer transition-all hover:scale-105",
            "border-0 px-3 py-1",
            community.is_fomo_official && "ring-1 ring-primary/20"
          )}
          style={{
            background: community.gradient || community.color || 'hsl(var(--muted))',
            color: community.gradient || community.color ? 'white' : 'inherit'
          }}
          onClick={() => handleClick(community)}
        >
          <div className="flex items-center gap-1">
            {community.is_fomo_official && (
              <Sparkles className="w-3 h-3" />
            )}
            {community.icon && <span>{community.icon}</span>}
            <span className="font-medium">{community.display_name}</span>
          </div>
        </Badge>
      ))}
    </div>
  );
}

export default CommunityBadges;
